import { useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import {
  ONBOARDING_STATUS,
  hasActiveModuleGrant,
} from '@realadvisor/constants';
import { Check, Globe, SkipForward } from 'lucide-react';

import {
  type GetUserQuery,
  User_Onboarding_Skip_Reason_Enum_Enum,
  User_Onboarding_Status_Enum_Enum,
} from '@graphql/__generated__/graphql';
import { ConfirmationDialog } from '@modules/_shared/components/ConfirmationDialog';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { getSlugConstraintError } from '@modules/_shared/components/visibility-drawer/types';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { generateAgentSlug } from '@modules/_shared/utils/agentSlug';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import { OnboardingStatusChip } from '@modules/users/components/OnboardingStatusChip';
import {
  type OnboardingStep,
  type OnboardingStepState,
  getOnboardingSteps,
} from '@modules/users/user-overview/utils/onboardingSteps';
import {
  APPROVE_USER_ONBOARDING,
  GET_USER,
  GET_USERS_LIST,
  GET_USERS_LIST_COUNT,
  GET_USER_ONBOARDING_HISTORY,
  SKIP_USER_ONBOARDING,
} from '@modules/users/userQueries';
import { PUBLISH_USER } from '@modules/visibility-team/visibilityTeamQueries';
import { MODULES_VISIBILITY } from '@shared/constants/module';
import { useLocale } from '@shared/hooks/locale';

type User = NonNullable<GetUserQuery['users_by_pk']>;

type OnboardingStatusCardProps = {
  user: User;
};

const DOT_SIZE = 20;

// users.onboarding_skip_reason (hasura-crm 1793346929000): read by the
// onboarding dashboard to set aside agents who will never be published.
const getSkipReasons = (
  t: ReturnType<typeof useLocale>['t'],
): { value: User_Onboarding_Skip_Reason_Enum_Enum; label: string }[] => [
  {
    value: User_Onboarding_Skip_Reason_Enum_Enum.BuiltByCsm,
    label: t('Profile built by CSM'),
  },
  {
    value: User_Onboarding_Skip_Reason_Enum_Enum.CrmAccessOnly,
    label: t('CRM access only'),
  },
  {
    value: User_Onboarding_Skip_Reason_Enum_Enum.ExistingClient,
    label: t('Existing client'),
  },
  { value: User_Onboarding_Skip_Reason_Enum_Enum.Other, label: t('Other') },
];

const StepDot = ({ state }: { state: OnboardingStepState }) => {
  if (state === 'done' || state === 'skipped') {
    return (
      <Box
        sx={{
          width: DOT_SIZE,
          height: DOT_SIZE,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'common.white',
          bgcolor: state === 'done' ? 'success.main' : 'grey.400',
        }}
      >
        {state === 'done' ? (
          <Check size={12} strokeWidth={3} />
        ) : (
          <SkipForward size={10} strokeWidth={3} />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: DOT_SIZE,
        height: DOT_SIZE,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid',
        borderColor: state === 'current' ? 'warning.main' : 'grey.300',
      }}
    >
      {state === 'current' && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'warning.main',
          }}
        />
      )}
    </Box>
  );
};

const StepList = ({
  steps,
  skipReason,
}: {
  steps: OnboardingStep[];
  skipReason: User_Onboarding_Skip_Reason_Enum_Enum | null;
}) => {
  const { t, locale } = useLocale();
  // Skips made before the reason was asked have none.
  const skipReasonLabel =
    getSkipReasons(t).find(reason => reason.value === skipReason)?.label ??
    null;

  // A step reads as done only once it is: "Pending …" until then, so the
  // title alone says what has happened and what is still to come.
  const getLabel = (step: OnboardingStep) => {
    const pending = step.state === 'current' || step.state === 'upcoming';
    switch (step.key) {
      case 'started':
        return t('Started');
      case 'submitted':
        if (step.state === 'skipped') {
          return t('Submission skipped');
        }
        return pending ? t('Pending submission') : t('Submitted');
      case 'approved':
        return pending ? t('Pending approval') : t('Approved');
      case 'published':
        return pending ? t('Pending publication') : t('Published');
    }
  };

  const getCaption = (step: OnboardingStep) => {
    if (step.state === 'current') {
      return step.key === 'submitted'
        ? t('Waiting for the agent')
        : t('Waiting for a CSM');
    }
    const date =
      step.at == null
        ? null
        : toHumanReadableDate(locale, step.at, { month: 'short' });
    // Only the CSM steps name who did them: the rest is the agent or the system.
    const by =
      step.key === 'approved' || step.key === 'published' ? step.by : null;
    const reason = step.state === 'skipped' ? skipReasonLabel : null;
    return [date, by, reason].filter(Boolean).join(' · ') || null;
  };

  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {steps.map((step, index) => {
        const next = steps[index + 1];
        const caption = getCaption(step);
        return (
          <Stack
            component="li"
            key={step.key}
            direction="row"
            sx={{ gap: 1.5 }}
          >
            <Stack sx={{ alignItems: 'center', pt: 0.25 }}>
              <StepDot state={step.state} />
              {next != null && (
                <Box
                  sx={{
                    width: 2,
                    flexGrow: 1,
                    minHeight: 12,
                    my: 0.5,
                    borderRadius: 1,
                    bgcolor:
                      next.state === 'done' || next.state === 'skipped'
                        ? 'success.light'
                        : 'divider',
                  }}
                />
              )}
            </Stack>
            <Box sx={{ minWidth: 0, pb: next != null ? 1.5 : 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color:
                    step.state === 'upcoming'
                      ? 'text.disabled'
                      : 'text.primary',
                }}
              >
                {getLabel(step)}
              </Typography>
              {caption != null && (
                <Typography
                  variant="caption"
                  component="p"
                  sx={{
                    color:
                      step.state === 'current'
                        ? 'warning.dark'
                        : 'text.secondary',
                  }}
                >
                  {caption}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      })}
    </Box>
  );
};

const OnboardingStatusCardContent = ({
  user,
  status,
}: {
  user: User;
  status: User_Onboarding_Status_Enum_Enum;
}) => {
  const { t } = useLocale();
  const [confirmSkip, setConfirmSkip] = useState(false);
  const [skipReason, setSkipReason] =
    useState<User_Onboarding_Skip_Reason_Enum_Enum | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const { data } = useQuery(GET_USER_ONBOARDING_HISTORY, {
    variables: { id: user.id },
  });
  const refetchQueries = [
    GET_USER,
    GET_USERS_LIST,
    GET_USERS_LIST_COUNT,
    GET_USER_ONBOARDING_HISTORY,
  ];
  const [approveOnboarding, approval] = useMutation(APPROVE_USER_ONBOARDING, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [skipOnboarding, skip] = useMutation(SKIP_USER_ONBOARDING, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [publishUser, publication] = useMutation(PUBLISH_USER, {
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const steps = getOnboardingSteps({
    userId: user.id,
    status,
    published: user.show_in_agency_pages === true,
    activities: data?.activities ?? [],
  });
  const publishPending = steps.some(
    step => step.key === 'published' && step.state === 'current',
  );

  // Same requirements as Publish in the agent's visibility drawer.
  const agentSlug =
    user.agent_slug ||
    generateAgentSlug(user.first_name ?? null, user.last_name ?? null);
  const publishBlockers = [
    !hasActiveModuleGrant(user.module_grants, MODULES_VISIBILITY) &&
      t('Visibility module required to publish'),
    user.user_images.length === 0 && t('Profile picture required to publish'),
    agentSlug == null && t('Unable to generate a profile URL from name'),
  ].filter((reason): reason is string => typeof reason === 'string');
  const slugError =
    publication.error == null
      ? null
      : getSlugConstraintError(publication.error, t);

  // The wizard is what creates the agent's team and makes them a broker. A
  // skipped agent without either would be sent straight back to it.
  const canSkip =
    user.is_broker === true &&
    user.teams_users.some(teamUser => teamUser.deactivated !== true);

  const skipReasons = getSkipReasons(t);

  const closeSkip = () => {
    setConfirmSkip(false);
    setSkipReason(null);
  };

  const onSkip = async () => {
    if (skipReason == null) {
      return;
    }
    try {
      await skipOnboarding({
        variables: { id: user.id, reason: skipReason },
      });
      closeSkip();
    } catch {
      // Shown by the snackbar below.
    }
  };

  const onPublish = async () => {
    if (agentSlug == null) {
      return;
    }
    try {
      await publishUser({ variables: { userId: user.id, agentSlug } });
      setConfirmPublish(false);
    } catch {
      // Shown by the snackbars below.
    }
  };

  return (
    <>
      <ListingAccordion
        // Remounts once the history shows a first publication is due, so the
        // card opens on the Publish button.
        key={publishPending ? 'publish' : 'review'}
        title={t('Onboarding status')}
        // Nothing left to do once approved and published: keep the page short.
        defaultExpanded={
          status !== ONBOARDING_STATUS.approved || publishPending
        }
        headerAction={<OnboardingStatusChip status={status} />}
      >
        <Stack sx={{ gap: 2 }}>
          <StepList
            steps={steps}
            skipReason={data?.users_by_pk?.onboarding_skip_reason ?? null}
          />

          {status === ONBOARDING_STATUS.inProgress && (
            <Stack sx={{ alignItems: 'flex-end', gap: 0.5 }}>
              <Button
                variant="outlined"
                startIcon={<SkipForward size={16} />}
                disabled={!canSkip}
                onClick={() => setConfirmSkip(true)}
              >
                {t('Skip self-onboarding')}
              </Button>
              {!canSkip && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('Add the agent to a team first.')}
                </Typography>
              )}
            </Stack>
          )}

          {status === ONBOARDING_STATUS.pendingReview && (
            <Stack sx={{ alignItems: 'flex-end' }}>
              <LoadingButton
                loading={approval.loading}
                variant="contained"
                disableElevation
                startIcon={<Check size={16} />}
                onClick={() =>
                  void approveOnboarding({ variables: { id: user.id } }).catch(
                    () => {
                      // Shown by the snackbar below.
                    },
                  )
                }
              >
                {t('Approve')}
              </LoadingButton>
            </Stack>
          )}

          {publishPending && (
            <Stack sx={{ alignItems: 'flex-end', gap: 0.5 }}>
              <Button
                variant="contained"
                disableElevation
                startIcon={<Globe size={16} />}
                disabled={publishBlockers.length > 0}
                onClick={() => setConfirmPublish(true)}
              >
                {t('Publish profile')}
              </Button>
              {publishBlockers.map(reason => (
                <Typography
                  key={reason}
                  variant="caption"
                  sx={{ color: 'text.secondary' }}
                >
                  {reason}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      </ListingAccordion>

      <SlideDialog
        open={confirmSkip}
        onClose={closeSkip}
        onSave={() => void onSkip()}
        title={t('Skip self-onboarding?')}
        loading={skip.loading}
        saveDisabled={skipReason == null}
        saveText={t('Skip self-onboarding')}
        saveIcon={<SkipForward />}
        maxWidth="xs"
      >
        <Stack sx={{ gap: 2, p: 2 }}>
          <Typography variant="body2">
            {t(
              '{{name}} will go straight to the CRM, without self-onboarding and without approval.',
              { name: user.full_name ?? '' },
            )}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t('Why skip it?')}
          </Typography>
          <RadioGroup
            value={skipReason ?? ''}
            onChange={event =>
              setSkipReason(
                skipReasons.find(reason => reason.value === event.target.value)
                  ?.value ?? null,
              )
            }
          >
            {skipReasons.map(reason => (
              <FormControlLabel
                key={reason.value}
                value={reason.value}
                control={<Radio size="small" />}
                label={reason.label}
              />
            ))}
          </RadioGroup>
        </Stack>
      </SlideDialog>
      <ConfirmationDialog
        open={confirmPublish}
        onClose={() => setConfirmPublish(false)}
        onConfirm={() => void onPublish()}
        loading={publication.loading}
        title={t('Publish profile')}
        message={t('Are you sure you want to publish {{name}}?', {
          name: user.full_name ?? '',
        })}
        confirmText={t('Publish')}
      />
      {slugError != null && (
        <RASnackbar
          message={slugError}
          severity="error"
          onClose={() => publication.reset()}
        />
      )}
      <MutationErrorSnackbar
        error={
          approval.error ??
          skip.error ??
          (slugError == null ? publication.error : null) ??
          null
        }
        onClose={() => {
          approval.reset();
          skip.reset();
          publication.reset();
        }}
      />
    </>
  );
};

// Admin-only: where an agent is in self-onboarding, the CSM's way out of it,
// and their first publication. Every status gets the card, because "nothing
// shown" used to read as "approved" while the agent was still stuck on the
// wizard. An agent enrolled without a status yet (has_done_onboarding false)
// has the wizard ahead of them: shown, and skippable, as in progress.
export const OnboardingStatusCard = ({ user }: OnboardingStatusCardProps) => {
  const { me } = useAppData();
  const status =
    user.onboarding_status ??
    (user.has_done_onboarding === false
      ? User_Onboarding_Status_Enum_Enum.InProgress
      : null);

  if (me?.is_admin !== true || status == null) {
    return null;
  }

  return <OnboardingStatusCardContent user={user} status={status} />;
};
